import { useState, useEffect, useCallback } from 'react';
import { Info, Save, ShieldAlert } from 'lucide-react';
import { LOJAS_AUDITAVEIS } from '../../../utils/empresas';
import BlocoEvidencia from './BlocoEvidencia';
import {
  ehNegativa, pendenciasDeEvidencia, mensagemDePendencias,
  montarEvidencias, irParaPergunta, novaChaveSessao,
} from './evidenciaUtils';

// =======================================================================
// Questionário VERSIONADO — Avaliação de Segurança do Alimento (SA) e
// Avaliação de Treinamento (TSV) — DEM-00014.
// -----------------------------------------------------------------------
// UM questionário para as DUAS gêmeas, dirigido pela VERSÃO do banco — não há
// mais perguntas/pesos/críticas hardcoded no front (isso era a tripla
// duplicação: aqui, em perguntas-formularios.js e secoes-formularios.js). A
// estrutura vem de GET ${apiBase}/versoes-publicadas/:id; o veto e o corte vêm
// da versão (E_CRITICA por pergunta, CORTE_VETO por versão).
//
// Props:
//   apiBase   base do formulário: '/api/auditoria' (SA) | '/api/avaliacao' (TSV)
//   cor       cor do módulo (SA #475569 · TSV #0284c7)
//   idVersao  a versão publicada escolhida no modal "Nova Avaliação"
//   titulo    rótulo do formulário
//   onConcluido  callback ao enviar (o hub volta para a lista)
// =======================================================================

// Régua de cor (a mesma da 360/SA): ≥90 Verde · 80–89,99 Amarelo · 70–79,99
// Laranja · <70 Vermelho. Recebe a fração 0..1.
const classificarCor = (p) => {
  if (p >= 0.90) return { nome: 'Verde', cor: '#059669', bg: '#d1fae5', icone: '🟢' };
  if (p >= 0.80) return { nome: 'Amarelo', cor: '#ca8a04', bg: '#fef9c3', icone: '🟡' };
  if (p >= 0.70) return { nome: 'Laranja', cor: '#ea580c', bg: '#ffedd5', icone: '🟠' };
  return { nome: 'Vermelho', cor: '#dc2626', bg: '#fee2e2', icone: '🔴' };
};

function FormularioQuestionario({ apiBase, cor = '#475569', idVersao, titulo = 'Avaliação', onConcluido = null }) {
  const [estrutura, setEstrutura] = useState(null); // { versao, areas:[{CODIGO,NOME,perguntas:[...]}] }
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState('');

  const [formData, setFormData] = useState({ loja: '', data: '', gerenteUnidade: '', gerentePlantao: '' });

  // Sem recorte por mercado: SA e TSV cobrem todas as lojas auditáveis. A divisão
  // Norte/Sul (cross-avaliação) ficou só na Avaliação 360 (DEM-00034).
  const [gerentesPorLoja, setGerentesPorLoja] = useState({});
  const [gerentesUnidadeAtivos, setGerentesUnidadeAtivos] = useState([]);
  const [gerentesPlantaoAtivos, setGerentesPlantaoAtivos] = useState([]);

  const [respostas, setRespostas] = useState({});
  const [observacoes, setObservacoes] = useState({});
  const [evidencias, setEvidencias] = useState({}); // { CODIGO: [url, ...] }
  const [chaveSessao] = useState(() => novaChaveSessao());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

  // Carrega a estrutura da versão publicada e inicializa os mapas de resposta.
  const carregar = useCallback(async () => {
    setCarregando(true); setErroCarga('');
    try {
      const r = await fetch(`${apiBase}/versoes-publicadas/${idVersao}`, { headers: auth() });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.message || 'Erro ao carregar a versão.');
      setEstrutura(data);
      const codigos = (data.areas || []).flatMap((a) => a.perguntas.map((p) => p.CODIGO));
      setRespostas(Object.fromEntries(codigos.map((c) => [c, ''])));
      setObservacoes(Object.fromEntries(codigos.map((c) => [c, ''])));
    } catch (e) {
      setErroCarga(e.message);
    } finally {
      setCarregando(false);
    }
  }, [apiBase, idVersao]);

  useEffect(() => { carregar(); }, [carregar]);

  // Gerentes por loja (mesmo endpoint dos dois formulários).
  useEffect(() => {
    fetch(`${apiBase}/gerentes`, { headers: auth() })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('gerentes'))))
      .then(setGerentesPorLoja)
      .catch(() => { /* seletor fica vazio; não trava o preenchimento */ });
  }, [apiBase]);

  useEffect(() => {
    if (formData.loja && gerentesPorLoja[formData.loja]) {
      setGerentesUnidadeAtivos(gerentesPorLoja[formData.loja].negocios);
      setGerentesPlantaoAtivos(gerentesPorLoja[formData.loja].plantao);
    } else {
      const neg = new Set(); const pla = new Set();
      Object.values(gerentesPorLoja).forEach((l) => {
        l.negocios.forEach((n) => neg.add(n)); l.plantao.forEach((n) => pla.add(n));
      });
      setGerentesUnidadeAtivos(Array.from(neg).sort());
      setGerentesPlantaoAtivos(Array.from(pla).sort());
    }
  }, [formData.loja, gerentesPorLoja]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const novo = { ...prev, [name]: value };
      if (name === 'loja') { novo.gerenteUnidade = ''; novo.gerentePlantao = ''; }
      return novo;
    });
  };

  const responder = (key, valor) => setRespostas((prev) => ({ ...prev, [key]: valor }));

  if (carregando) return <p style={{ color: '#64748b' }}>Carregando o formulário…</p>;
  if (erroCarga) return <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8 }}>{erroCarga}</div>;

  // ---- Derivados da versão (fonte da verdade do veto/nota) ----
  const areas = estrutura.areas || [];
  const todasPerguntas = areas.flatMap((a) => a.perguntas);
  const pontuadas = todasPerguntas.filter((p) => p.PONTUADA).map((p) => p.CODIGO);
  const criticas = todasPerguntas.filter((p) => p.E_CRITICA).map((p) => p.CODIGO);
  const corte = estrutura.versao.CORTE_VETO === null || estrutura.versao.CORTE_VETO === undefined
    ? null : Number(estrutura.versao.CORTE_VETO);
  const vetoAplicavel = corte !== null || criticas.length > 0;

  // ---- Resumo ao vivo (contagem pura, espelho do servidor) ----
  const contar = (codigos) => {
    let sim = 0; let aplic = 0;
    codigos.forEach((c) => {
      if (respostas[c] === 'Sim') { sim++; aplic++; }
      else if (respostas[c] === 'Não') { aplic++; }
    });
    return { sim, aplic };
  };
  const { sim: totalSim, aplic: totalAplic } = contar(pontuadas);
  const totalPct = totalAplic > 0 ? (totalSim / totalAplic) * 100 : 0;
  const criticaReprovada = criticas.some((c) => respostas[c] === 'Não');
  const reprovaNota = corte !== null && totalAplic > 0 && totalPct < corte;
  const status = vetoAplicavel ? (criticaReprovada || reprovaNota ? 'IMPUR' : 'APROVADA') : null;

  const totalRespondidas = todasPerguntas.filter((p) => ['Sim', 'Não', 'N/A'].includes(respostas[p.CODIGO])).length;
  const pctProgresso = todasPerguntas.length > 0 ? Math.round((totalRespondidas / todasPerguntas.length) * 100) : 0;
  const algoRespondido = totalRespondidas > 0;

  const classeTotal = classificarCor(totalPct / 100);
  const statusInfo = !algoRespondido
    ? { cor: '#475569', bg: '#f1f5f9', icone: '⚪', nome: '—' }
    : status === 'IMPUR'
      ? { cor: '#dc2626', bg: '#fee2e2', icone: '🔴', nome: 'IMPUR' }
      : status === 'APROVADA'
        ? { cor: '#059669', bg: '#d1fae5', icone: '🟢', nome: 'APROVADA' }
        : { cor: classeTotal.cor, bg: classeTotal.bg, icone: classeTotal.icone, nome: classeTotal.nome };

  // Selo do card de cada área, conforme os flags das suas perguntas.
  const seloArea = (area) => {
    const crit = area.perguntas.filter((p) => p.E_CRITICA);
    if (crit.length > 0) {
      const nao = crit.filter((p) => respostas[p.CODIGO] === 'Não').length;
      if (nao > 0) return { texto: `⛔ ${nao} “Não” — reprova`, cor: '#dc2626', bg: '#fee2e2' };
      const resp = crit.filter((p) => ['Sim', 'Não', 'N/A'].includes(respostas[p.CODIGO])).length;
      if (resp === crit.length) return { texto: '🟢 Sem falha crítica', cor: '#059669', bg: '#d1fae5' };
      return { texto: `${resp}/${crit.length}`, cor: '#475569', bg: '#f1f5f9' };
    }
    const pont = area.perguntas.filter((p) => p.PONTUADA);
    if (pont.length > 0) {
      const { sim, aplic } = contar(pont.map((p) => p.CODIGO));
      if (aplic === 0) return { texto: '⚪ N/A', cor: '#475569', bg: '#f1f5f9' };
      const cls = classificarCor(sim / aplic);
      return { texto: `${cls.icone} ${sim}/${aplic} · ${Math.round((sim / aplic) * 100)}%`, cor: cls.cor, bg: cls.bg };
    }
    const resp = area.perguntas.filter((p) => ['Sim', 'Não', 'N/A'].includes(respostas[p.CODIGO])).length;
    return { texto: `${resp}/${area.perguntas.length}`, cor: '#475569', bg: '#f1f5f9' };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const naoRespondida = todasPerguntas.find((p) => !['Sim', 'Não', 'N/A'].includes(respostas[p.CODIGO]));
    if (naoRespondida) {
      alert('Responda todas as perguntas antes de enviar.');
      irParaPergunta(naoRespondida.CODIGO);
      return;
    }
    // Toda "Não" exige foto + observação (o servidor barra de novo — é a trava).
    const pendencias = pendenciasDeEvidencia(respostas, observacoes, evidencias);
    if (pendencias.length > 0) {
      alert(mensagemDePendencias(pendencias));
      irParaPergunta(pendencias[0].pergunta);
      return;
    }

    setIsSubmitting(true);
    const payload = {
      ID_VERSAO: idVersao,
      LOJA: formData.loja || '',
      DATA: formData.data || '',
      GERENTE_NEGOCIO: formData.gerenteUnidade || '',
      GERENTE_PLANTAO: formData.gerentePlantao || '',
      // Uma entrada por pergunta — a nota/veto são recalculados no servidor.
      respostas: todasPerguntas.map((p) => ({
        codigo: p.CODIGO, resposta: respostas[p.CODIGO] || '', observacao: observacoes[p.CODIGO] || '',
      })),
      evidencias: montarEvidencias(evidencias), // [{ pergunta: CODIGO, url }]
    };
    try {
      const r = await fetch(`${apiBase}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth() },
        body: JSON.stringify(payload),
      });
      if (r.ok) {
        if (onConcluido) onConcluido();
      } else {
        const erro = await r.json().catch(() => ({}));
        alert(erro?.message || 'Erro ao enviar.');
        if (erro?.pendencias?.length) irParaPergunta(erro.pendencias[0].pergunta);
        setIsSubmitting(false);
      }
    } catch (err) {
      setIsSubmitting(false);
      alert('Erro: ' + err.message);
    }
  };

  // Uma pergunta: rótulo (código + texto + selo crítica) + Sim/Não/N/A + evidência no "Não".
  const renderPergunta = (p) => {
    const resp = respostas[p.CODIGO];
    const negativa = ehNegativa(resp);
    return (
      <div key={p.CODIGO} data-pergunta={p.CODIGO} style={qBox}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 220px', gap: 12, alignItems: 'center' }}>
          <div style={{ color: '#0f172a', fontSize: 14 }}>
            <span style={{ fontWeight: 700, color: '#64748b', marginRight: 6 }}>{p.CODIGO}</span>
            {p.TEXTO}
            {!!p.E_CRITICA && (
              <span style={{ marginLeft: 8, padding: '1px 6px', borderRadius: 6, fontSize: 11, fontWeight: 800, background: '#fee2e2', color: '#b91c1c' }}>
                crítica
              </span>
            )}
            {!p.PONTUADA && !p.E_CRITICA && (
              <span style={{ marginLeft: 8, fontSize: 11, color: '#94a3b8' }}>(fora da nota)</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button type="button" onClick={() => responder(p.CODIGO, 'Sim')} style={botaoResp(resp === 'Sim', '#059669')}>Sim</button>
            <button type="button" onClick={() => responder(p.CODIGO, 'Não')} style={botaoResp(resp === 'Não', '#dc2626')}>Não</button>
            <button type="button" onClick={() => responder(p.CODIGO, 'N/A')} style={botaoResp(resp === 'N/A', '#64748b')}>N/A</button>
          </div>
        </div>
        {negativa && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed #e2e8f0' }}>
            <BlocoEvidencia
              api={apiBase}
              chave={p.CODIGO}
              observacao={observacoes[p.CODIGO]}
              onObservacao={(texto) => setObservacoes((prev) => ({ ...prev, [p.CODIGO]: texto }))}
              urls={evidencias[p.CODIGO] || []}
              onUrls={(urls) => setEvidencias((prev) => ({ ...prev, [p.CODIGO]: urls }))}
              meta={{ loja: formData.loja, data: formData.data, chave: chaveSessao }}
              bloqueado={!formData.loja || !formData.data}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit} style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
        <ShieldAlert size={22} color={cor} />
        <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>{titulo}</h3>
      </div>
      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>Versão: <strong>{estrutura.versao.NOME_VERSAO}</strong></div>

      {/* Como o resultado é decidido — gerado a partir das regras DESTA versão. */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 16,
        background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '12px 14px', color: '#1e40af', fontSize: 13,
      }}>
        <Info size={18} style={{ flexShrink: 0, marginTop: 1 }} />
        <span>
          Percentual = <strong>Sim ÷ (Sim + Não)</strong> das perguntas que pontuam · N/A fora do denominador.
          Cada pergunta marcada <strong>“Não”</strong> exige <strong>foto e observação</strong>.
          {criticas.length > 0 && <> Um <strong>“Não”</strong> em qualquer das <strong>{criticas.length} questões críticas</strong> reprova (<strong>IMPUR</strong>).</>}
          {corte !== null && <> Nota <strong>abaixo de {corte}%</strong> também reprova.</>}
          {!vetoAplicavel && <> Este formulário não tem veto — entrega só percentual e cor.</>}
        </span>
      </div>

      {/* Dados da avaliação */}
      <div style={cardBox}>
        <h4 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 800, color: cor }}>Dados da Avaliação</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          <div>
            <label htmlFor="loja" style={lbl}>Loja *</label>
            <select id="loja" name="loja" value={formData.loja} onChange={handleFormChange} required style={inp}>
              <option value="">Selecione a Loja</option>
              {LOJAS_AUDITAVEIS.map((loja) => <option key={loja} value={loja}>{loja}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="data" style={lbl}>Data *</label>
            <input id="data" type="date" name="data" value={formData.data} onChange={handleFormChange} required style={inp} />
          </div>
          <div>
            <label htmlFor="gerenteUnidade" style={lbl}>Gerente da unidade de negócio *</label>
            <select id="gerenteUnidade" name="gerenteUnidade" value={formData.gerenteUnidade} onChange={handleFormChange} required style={inp}>
              <option value="">Selecione um Gerente</option>
              {gerentesUnidadeAtivos.map((nome) => <option key={nome} value={nome}>{nome}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="gerentePlantao" style={lbl}>Gerente de plantão *</label>
            <select id="gerentePlantao" name="gerentePlantao" value={formData.gerentePlantao} onChange={handleFormChange} required style={inp}>
              <option value="">Selecione um Gerente</option>
              {gerentesPlantaoAtivos.map((nome) => <option key={nome} value={nome}>{nome}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Resultado ao vivo + progresso (sticky) */}
      <div style={{ position: 'sticky', top: 0, zIndex: 5, background: statusInfo.bg, border: `2px solid ${statusInfo.cor}`, borderRadius: 12, padding: 16, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 28 }}>{statusInfo.icone}</div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: '#475569', letterSpacing: 0.5 }}>RESULTADO GERAL</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: statusInfo.cor }}>
              {algoRespondido ? `${totalSim} / ${totalAplic} · ${totalPct.toFixed(2)}% · ${classeTotal.nome}` : 'Ainda sem respostas'}
            </div>
            {criticaReprovada && (
              <div style={{ fontSize: 12, fontWeight: 700, color: '#b91c1c', marginTop: 2 }}>Reprovada por questão crítica marcada “Não”.</div>
            )}
          </div>
          {algoRespondido && status && (
            <div style={{ padding: '6px 14px', borderRadius: 999, fontWeight: 800, fontSize: 14, background: '#fff', color: statusInfo.cor, border: `2px solid ${statusInfo.cor}` }}>
              {statusInfo.nome}
            </div>
          )}
        </div>
        <div style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 4 }}>
            <span>Progresso</span>
            <span>{totalRespondidas} / {todasPerguntas.length} · {pctProgresso}%</span>
          </div>
          <div style={{ height: 8, background: 'rgba(255,255,255,.6)', borderRadius: 999, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pctProgresso}%`, borderRadius: 999, background: statusInfo.cor, transition: 'width .2s' }} />
          </div>
        </div>
      </div>

      {/* Áreas (cards) */}
      {areas.map((area) => {
        const selo = seloArea(area);
        return (
          <div key={area.CODIGO || area.NOME} style={cardBox}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
              <h4 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#0f172a' }}>{area.NOME}</h4>
              <div style={{ padding: '4px 10px', borderRadius: 8, fontSize: 13, fontWeight: 800, background: selo.bg, color: selo.cor }}>{selo.texto}</div>
            </div>
            <div style={{ display: 'grid', gap: 10 }}>
              {area.perguntas.map((p) => renderPergunta(p))}
            </div>
          </div>
        );
      })}

      <button type="submit" disabled={isSubmitting} style={{
        display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 24px',
        background: isSubmitting ? '#94a3b8' : cor, color: '#fff', border: 'none', borderRadius: 8,
        fontWeight: 800, fontSize: 15, cursor: isSubmitting ? 'not-allowed' : 'pointer',
      }}>
        <Save size={18} /> {isSubmitting ? 'Salvando…' : 'Enviar Avaliação'}
      </button>
    </form>
  );
}

const cardBox = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, marginBottom: 16 };
const qBox = { padding: 12, background: '#f8fafc', borderRadius: 8 };
const lbl = { fontSize: 12, fontWeight: 700, color: '#64748b' };
const inp = { width: '100%', padding: 8, marginTop: 4, border: '1px solid #cbd5e1', borderRadius: 6 };
const botaoResp = (selecionado, cor) => ({
  flex: 1, padding: '8px 12px', borderRadius: 8,
  border: `2px solid ${selecionado ? cor : '#e2e8f0'}`,
  background: selecionado ? cor : '#fff',
  color: selecionado ? '#fff' : '#475569',
  fontWeight: 700, cursor: 'pointer', fontSize: 13, transition: 'all .12s',
});

export default FormularioQuestionario;
